import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@/lib/useIsMobile";
import { MobileDecisions } from "@/mobile/MobileDecisions";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InboxItemDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  PageHeader,
  Panel,
  emphasis,
  toneColor,
  type Tone,
} from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { openJarvis } from "@/jarvis/jarvis-store";
import {
  ALL_PROJECTS,
  ProjectFilterTabs,
  filterByProject,
  projectGroups,
  resolveProjectFilter,
} from "./project-filter";

const urgencyTone: Record<string, Tone> = {
  URGENT: "red",
  HIGH: "amber",
  NORMAL: "cyan",
  LOW: "dim",
};

/** Decision Inbox (spec §32, /Design): seznam vlevo, detail s akcemi vpravo. */
export function DecisionInboxPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileDecisions />;
  return <DesktopDecisionInbox />;
}

function DesktopDecisionInbox() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["inbox"],
    queryFn: () => api.decisionInbox(),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();
  // filtr žije v adrese, ať se rozpracovaný pohled dá poslat odkazem
  const { project: rawProjectFilter = ALL_PROJECTS } = useSearch({
    from: "/shell/decisions",
  });
  const [toast, setToast] = useState<{ msg: string; undoId?: string } | null>(
    null,
  );
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string, undoId?: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, undoId });
    // s undo nechávám delší okno, ať je čas se rozmyslet
    toastTimer.current = setTimeout(() => setToast(null), undoId ? 8000 : 4000);
  };

  const undoMutation = useMutation({
    mutationFn: (id: string) => api.reopenDecision(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      showToast(cs.decisions.undoneToast);
    },
    onError: (err) =>
      showToast(err instanceof Error ? err.message : cs.common.error),
  });

  const allItems = query.data?.items ?? [];
  // chipy počítáme ze všech položek, seznam a detail už jen z odfiltrovaných
  const projectFilter = resolveProjectFilter(
    projectGroups(allItems),
    rawProjectFilter,
  );
  const items = filterByProject(allItems, projectFilter);
  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null;

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.decisions.title}
        kicker="INBOX"
        lead={cs.decisions.lead(items.length)}
        right={
          <ProjectFilterTabs
            items={allItems}
            value={projectFilter}
            onChange={(key) => {
              void navigate({
                to: "/decisions",
                search: key === ALL_PROJECTS ? {} : { project: key },
              });
              setSelectedId(null);
            }}
          />
        }
      />
      <div className="grid min-h-[560px] grid-cols-1 items-start gap-5 xl:grid-cols-[400px_1fr]">
        {/* seznam */}
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className="cursor-pointer px-[18px] py-4 text-left transition-colors"
              style={{
                borderRadius: "var(--radius-card)",
                border: `1px solid ${selected?.id === item.id ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                background:
                  selected?.id === item.id
                    ? "var(--surface-raised)"
                    : "var(--surface)",
                backdropFilter: "var(--surface-blur)",
                boxShadow:
                  selected?.id === item.id ? emphasis("cyan") : undefined,
              }}
              data-testid="inbox-item"
            >
              <div className="af-mono flex justify-between text-[12px] tracking-[0.1em]">
                <span
                  className="font-bold"
                  style={{
                    color: toneColor[urgencyTone[item.urgency] ?? "cyan"],
                  }}
                >
                  {item.urgency}
                  {/* Návrhy sem už nechodí — mají vlastní obrazovku. Zbývá
                      konflikt a rozhodnutí vzniklé z blokace workera. */}
                  {item.itemType === "CONFLICT"
                    ? ` · ${cs.decisions.conflictTag}`
                    : item.blocksSummary === "z blokace workera"
                      ? ` · ${cs.decisions.blockerTag}`
                      : ""}
                  {item.decision?.blocksProject ? (
                    <span style={{ color: "var(--critical)" }}>
                      {" "}
                      · {cs.decisions.blocksProjectTag}
                    </span>
                  ) : null}
                </span>
                <span style={{ color: "var(--text-tertiary)" }}>
                  {relativeTime(item.createdAt)}
                </span>
              </div>
              <div className="af-card-title mb-[3px] mt-[5px] leading-snug">
                {item.title}
              </div>
              <div
                className="text-[12px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {item.projectName ?? "Factory"}
                {item.blocksSummary ? ` · ${item.blocksSummary}` : ""}
              </div>
            </button>
          ))}
          {items.length === 0 ? (
            <div
              className="mt-auto px-[18px] py-4 text-center text-[12px]"
              style={{
                borderRadius: "var(--radius-card)",
                border:
                  "1px dashed color-mix(in srgb, var(--positive) 35%, transparent)",
                color: "var(--text-tertiary)",
              }}
            >
              <span style={{ color: "var(--positive)" }}>✓ </span>
              {cs.decisions.emptyTitle} — {cs.decisions.emptyNote}
            </div>
          ) : null}
        </div>

        {/* detail */}
        <div
          className="overflow-auto p-7"
          style={{
            borderRadius: "var(--radius-panel)",
            border: "1px solid var(--border-default)",
            background: "var(--surface)",
            backdropFilter: "var(--surface-blur)",
          }}
        >
          {toast ? (
            <div
              className="mb-3 flex items-center justify-between gap-3 px-4 py-2 text-[12px]"
              style={{
                borderRadius: "var(--radius-box)",
                border:
                  "1px solid color-mix(in srgb, var(--positive) 30%, transparent)",
                color: "var(--positive)",
              }}
              data-testid="inbox-toast"
            >
              <span>✓ {toast.msg}</span>
              {toast.undoId ? (
                <Btn
                  variant="secondary"
                  tone="amber"
                  className="whitespace-nowrap !px-3 !py-1 !text-[12px]"
                  disabled={undoMutation.isPending}
                  onClick={() => undoMutation.mutate(toast.undoId!)}
                  data-testid="inbox-undo"
                >
                  ↩ {cs.decisions.undo}
                </Btn>
              ) : null}
            </div>
          ) : null}
          {selected ? (
            <InboxDetail
              key={selected.id}
              item={selected}
              onResolved={(msg, undoId) => {
                showToast(msg, undoId);
                setSelectedId(null);
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div
                  className="af-display text-[24px] font-light"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {cs.decisions.emptyDetail}
                </div>
                <div
                  className="mt-2 text-[12px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {cs.decisions.emptyDetailNote}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InboxDetail({
  item,
  onResolved,
}: {
  item: InboxItemDTO;
  onResolved: (msg: string, undoId?: string) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<{
    resolution: string;
    label: string;
  } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["inbox"] });
    if (item.projectId) {
      void queryClient.invalidateQueries({
        queryKey: ["section", item.projectId],
      });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    }
  };

  /**
   * Přeřazení položky, která rozhodnutí není. Bez toho by úklid inboxu
   * znamenal buď ruční přepisování, nebo automatiku podle klíčových slov —
   * a ta by „zkontroluj, jestli Stripe, nebo GoPay" (rozhodnutí) přeřadila
   * stejně jako „zkontroluj pásky" (úkol). Rozhoduje člověk, po jedné.
   */
  const reclassifyMutation = useMutation({
    mutationFn: (target: "HUMAN_TASK" | "PROPOSAL") =>
      api.reclassifyDecision(item.id, { target }),
    onSuccess: (_data, target) => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ["human-tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["proposals-inbox"] });
      onResolved(
        cs.decisions.reclassifiedToast(
          target === "HUMAN_TASK" ? "úkol pro člověka" : "návrh",
        ),
      );
      void navigate({ to: target === "HUMAN_TASK" ? "/human-tasks" : "/proposals" });
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  const resolveMutation = useMutation({
    mutationFn: (input: { resolution: string; note?: string }) =>
      api.resolveDecision(item.id, input),
    onSuccess: (data, variables) => {
      invalidate();
      // schválený proposal vrátit nejde (vznikl task) — undo nenabízíme
      const approvedProposal =
        item.decision?.kind === "PROPOSAL" &&
        variables.resolution !== "REJECTED" &&
        variables.resolution !== "CUSTOM";
      onResolved(
        cs.decisions.resolvedToast(data.unblockedCount),
        approvedProposal ? undefined : item.id,
      );
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  /** Každé rozhodnutí jde přes potvrzovací okno; poznámka se přikládá vždy. */
  const requestResolve = (resolution: string, label: string) => {
    setError(null);
    setConfirming({ resolution, label });
  };
  const confirmResolve = () => {
    if (!confirming) return;
    const trimmed = note.trim();
    resolveMutation.mutate({
      resolution: confirming.resolution,
      ...(trimmed ? { note: trimmed } : {}),
    });
    setConfirming(null);
  };

  const canDecide = session?.user.role !== "CLIENT";
  const urgTone = urgencyTone[item.urgency] ?? "cyan";
  const d = item.decision;
  /** Nemá doporučení, nemá varianty a nic neblokuje — na rozhodnutí to nevypadá. */
  const looksMisfiled = Boolean(
    d && !d.recommendation.trim() && d.alternatives.length === 0 && d.blockedTasks.length === 0,
  );

  return (
    <div className="max-w-[860px]" data-testid="inbox-detail">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="af-mono flex flex-wrap items-center gap-2 text-[11px]">
          <Chip tone={urgTone}>
            {urgTone === "red" ? "⛔ " : urgTone === "amber" ? "▲ " : ""}
            {item.urgency}
          </Chip>
          {d?.blocksProject ? (
            <span
              data-testid="inbox-blocks-project"
              title={cs.decisions.blocksProjectNote}
            >
              <Chip tone="red">{cs.decisions.blocksProjectTag}</Chip>
            </span>
          ) : null}
          <span style={{ color: "var(--text-tertiary)" }}>
            {item.projectName ?? "Factory"} · {relativeTime(item.createdAt)}
          </span>
        </div>
        <span
          className="af-mono text-[11px]"
          style={{ color: "var(--text-tertiary)" }}
        >
          {item.itemType === "CONFLICT" ? "CONFLICT" : "DECISION"} #
          {item.id.slice(-5)}
        </span>
      </div>
      <h2
        className="af-display mb-5 mt-2 text-[26px] font-bold leading-tight"
        style={{ color: "var(--text-primary)" }}
      >
        {item.title}
      </h2>

      {item.itemType === "CONFLICT" && item.conflict ? (
        <>
          <Panel className="mb-3 p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.decisions.context}
            </div>
            <p
              className="text-[13px] leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              {item.conflict.description}
            </p>
          </Panel>
          <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Panel className="p-3.5">
              <div className="af-label-dim mb-1">
                A · {item.conflict.optionA.label}
              </div>
              <div
                className="text-[11px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {item.conflict.optionA.detail ?? ""}{" "}
                {item.conflict.optionA.sourceRef
                  ? `· ${item.conflict.optionA.sourceRef}`
                  : ""}
              </div>
            </Panel>
            <Panel
              tone={item.conflict.recommended === "B" ? "green" : undefined}
              className="p-3.5"
            >
              <div className="af-label-dim mb-1">
                B · {item.conflict.optionB.label}
              </div>
              <div
                className="text-[11px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {item.conflict.optionB.detail ?? ""}{" "}
                {item.conflict.optionB.sourceRef
                  ? `· ${item.conflict.optionB.sourceRef}`
                  : ""}
              </div>
            </Panel>
          </div>
          <Btn
            variant="primary"
            onClick={() =>
              void navigate({
                to: "/projects/$slug/$tab",
                params: { slug: item.projectSlug!, tab: "knowledge" },
              })
            }
          >
            {cs.decisions.conflictGoto}
          </Btn>
          <div
            className="mt-3 text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            Konflikt se řeší v Knowledge — rozhodnutí zapíše revizi s
            provenancí.
          </div>
        </>
      ) : d ? (
        <>
          <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Panel className="p-4">
              <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
                {cs.decisions.context}
              </div>
              <p
                className="text-[13px] leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {d.context || "—"}
              </p>
            </Panel>
            <Panel tone="green" className="p-4">
              <div
                className="af-label mb-2"
                style={{ color: "var(--positive)" }}
              >
                {cs.decisions.recommendation}
              </div>
              <div
                className="text-[13px] font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                {d.recommendation || "—"}
              </div>
              {d.reasoning ? (
                <p
                  className="mt-1.5 text-[12px] leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {d.reasoning}
                </p>
              ) : null}
            </Panel>
          </div>

          {d.alternatives.length > 0 ? (
            <Panel className="mb-3 p-4">
              <div
                className="af-label mb-2.5"
                style={{ color: "var(--accent)" }}
              >
                {cs.decisions.alternatives}
              </div>
              <div className="flex flex-col gap-2">
                {d.alternatives.map((alt) => (
                  <button
                    key={alt.key}
                    type="button"
                    className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3 text-left transition-colors"
                    style={{
                      borderRadius: "var(--radius-row)",
                      border: "1px solid var(--border-default)",
                      background: "var(--surface-inset)",
                    }}
                    onClick={() =>
                      requestResolve(
                        alt.key,
                        cs.decisions.confirmAlternative(alt.label),
                      )
                    }
                    data-testid={`inbox-alt-${alt.key}`}
                  >
                    <span>
                      <span
                        className="block text-[13px] font-semibold"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {alt.label}
                      </span>
                      <span
                        className="mt-0.5 block text-[12px]"
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        {alt.impact}
                      </span>
                    </span>
                    <span
                      className="af-mono whitespace-nowrap text-[12px] tracking-[0.16em]"
                      style={{ color: "var(--accent)" }}
                    >
                      {cs.decisions.choose}
                    </span>
                  </button>
                ))}
              </div>
            </Panel>
          ) : null}

          <Panel className="mb-3 p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.decisions.customLabel}
            </div>
            <textarea
              className="w-full resize-y bg-transparent px-3 py-2 font-mono text-[11px] outline-none"
              style={{
                borderRadius: "var(--radius-box)",
                border: "1px solid var(--border-default)",
                color: "var(--text-primary)",
                minHeight: 64,
              }}
              placeholder={cs.decisions.customPlaceholder}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="inbox-note"
            />
            <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
              <div
                className="max-w-[520px] text-[11px] leading-relaxed"
                style={{ color: "var(--text-tertiary)" }}
              >
                {cs.decisions.customHint}
              </div>
              {/* p1a: „Odeslat vlastní řešení" nese plochu tónu, ne rám */}
              <Btn
                variant="secondary"
                tone="violet"
                className="whitespace-nowrap"
                disabled={
                  resolveMutation.isPending || !canDecide || !note.trim()
                }
                onClick={() =>
                  requestResolve("CUSTOM", cs.decisions.confirmCustom)
                }
                data-testid="inbox-custom"
              >
                {cs.decisions.customSubmit}
              </Btn>
            </div>
          </Panel>

          <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Panel tone="amber" className="p-3.5">
              <div
                className="af-label mb-1.5"
                style={{ color: "var(--warning)" }}
              >
                {cs.decisions.blocks}
              </div>
              <div
                className="text-[12px] leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {d.blockedTasks.length
                  ? d.blockedTasks.map((t) => (
                      <Link
                        key={t.id}
                        to="/projects/$slug/tasks/$number"
                        params={{
                          slug: item.projectSlug ?? "",
                          number: String(t.number),
                        }}
                        className="block"
                      >
                        #{t.number} {t.title}
                      </Link>
                    ))
                  : "nic — informativní rozhodnutí"}
              </div>
              {d.blocksProject ? (
                <div
                  className="mt-2 text-[12px] leading-relaxed"
                  style={{ color: "var(--critical)" }}
                >
                  {cs.decisions.blocksProjectNote}
                </div>
              ) : null}
            </Panel>
            <Panel className="p-3.5">
              <div
                className="af-label mb-1.5"
                style={{ color: "var(--accent)" }}
              >
                {cs.decisions.impactTitle}
              </div>
              <div
                className="text-[12px] leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {d.impact || "—"}
              </div>
            </Panel>
          </div>

          {/*
            Nápověda, ne automatika: prázdné doporučení, žádné alternativy
            a nic to neblokuje — tak vypadala v inboxu „zkontroluj pásky".
            Rozhodnout, kam to patří, musí člověk; heuristika by na téhle
            trojici stejně dobře označila i legitimní rozhodnutí.
          */}
          <Panel className="mb-4 p-4" tone={looksMisfiled ? "amber" : undefined}>
            <div
              className="af-label mb-1.5"
              style={{ color: looksMisfiled ? "var(--warning)" : "var(--accent)" }}
            >
              {cs.decisions.reclassify}
            </div>
            <div
              className="mb-2.5 text-[11px] leading-relaxed"
              style={{ color: "var(--text-tertiary)" }}
            >
              {looksMisfiled ? `${cs.decisions.looksLikeHumanTask} ` : ""}
              {cs.decisions.reclassifyHint}
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Btn
                variant="secondary"
                tone="amber"
                className="whitespace-nowrap"
                disabled={reclassifyMutation.isPending || !canDecide}
                onClick={() => reclassifyMutation.mutate("HUMAN_TASK")}
                title={cs.decisions.reclassifyHumanTaskNote}
                data-testid="inbox-reclassify-human-task"
              >
                {cs.decisions.reclassifyToHumanTask}
              </Btn>
              <Btn
                variant="secondary"
                tone="violet"
                className="whitespace-nowrap"
                disabled={reclassifyMutation.isPending || !canDecide || Boolean(d.proposalId)}
                onClick={() => reclassifyMutation.mutate("PROPOSAL")}
                title={cs.decisions.reclassifyProposalNote}
                data-testid="inbox-reclassify-proposal"
              >
                {cs.decisions.reclassifyToProposal}
              </Btn>
            </div>
          </Panel>

          {error ? (
            <div
              className="mb-2 text-[12px]"
              style={{ color: "var(--critical)" }}
            >
              ⚠ {error}
            </div>
          ) : null}

          {/* p1a: hlavní akce obrazovky — gradient, výška 44, poloměr 12 */}
          <div className="flex flex-wrap gap-2.5">
            <Btn
              variant="primary"
              size="lg"
              disabled={resolveMutation.isPending || !canDecide}
              onClick={() =>
                requestResolve("RECOMMENDED", cs.decisions.confirmApprove)
              }
              data-testid="inbox-approve"
            >
              {cs.decisions.approve}
            </Btn>
            <Btn
              variant="danger"
              size="lg"
              disabled={resolveMutation.isPending}
              onClick={() =>
                requestResolve("REJECTED", cs.decisions.confirmReject)
              }
              data-testid="inbox-reject"
            >
              {cs.decisions.reject}
            </Btn>
            <Btn
              size="lg"
              onClick={() =>
                openJarvis(cs.decisions.discussPrefill(item.title))
              }
              data-testid="inbox-discuss"
            >
              {cs.decisions.discuss}
            </Btn>
          </div>
          <div
            className="mt-3 text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.decisions.afterNote}
          </div>

          {confirming ? (
            <ConfirmResolveModal
              itemTitle={item.title}
              label={confirming.label}
              note={note.trim()}
              danger={confirming.resolution === "REJECTED"}
              pending={resolveMutation.isPending}
              onConfirm={confirmResolve}
              onCancel={() => setConfirming(null)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Potvrzovací okno před vyřešením rozhodnutí — chrání před překliknutím. */
function ConfirmResolveModal({
  itemTitle,
  label,
  note,
  danger,
  pending,
  onConfirm,
  onCancel,
}: {
  itemTitle: string;
  label: string;
  note: string;
  danger: boolean;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // portál do <body>: stránkový wrapper má transform animaci (af-animate-fade,
  // fill both) → byl by containing blockem a overlay by se centroval podle
  // stránky, ne podle viditelného viewportu (muselo by se k němu scrollovat)
  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center overflow-y-auto p-3"
      style={{ background: "var(--scrim)" }}
      onClick={onCancel}
      data-testid="inbox-confirm-modal"
    >
      {/* modál podle p1c: poloměr 20, odsazení 28, akcentní okraj a záře */}
      <div
        className="my-auto w-[520px] max-w-[calc(100vw-24px)] p-7"
        style={{
          borderRadius: "var(--radius-panel)",
          border: `1px solid ${danger ? "var(--critical-border)" : "var(--accent-border)"}`,
          background: "var(--surface-overlay)",
          backdropFilter: "var(--surface-blur)",
          boxShadow: danger
            ? "var(--emphasis-critical)"
            : "var(--emphasis-accent)",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex flex-col gap-3"
        >
          <div
            className="af-mono text-[11px] tracking-[0.2em]"
            style={{ color: danger ? "var(--critical)" : "var(--accent)" }}
          >
            {cs.decisions.confirmTitle}
          </div>
          <div
            className="af-display text-[20px] font-bold leading-snug"
            style={{ color: "var(--text-primary)" }}
          >
            {itemTitle}
          </div>
          <div
            className="px-4 py-3 text-[13px] font-semibold"
            style={{
              borderLeft: `3px solid ${danger ? "var(--critical)" : "var(--positive)"}`,
              borderRadius: "0 var(--radius-control) var(--radius-control) 0",
              background: danger
                ? "var(--critical-subtle)"
                : "var(--positive-subtle)",
              color: "var(--text-primary)",
            }}
          >
            {label}
          </div>
          {note ? (
            <div>
              <div className="af-label-dim mb-1">
                {cs.decisions.confirmNoteLabel}
              </div>
              <div
                className="max-h-[120px] overflow-auto px-3 py-2 text-[12px] leading-relaxed"
                style={{
                  borderRadius: "var(--radius-box)",
                  border: "1px solid var(--border-default)",
                  color: "var(--text-primary)",
                }}
              >
                {note}
              </div>
            </div>
          ) : null}
          <div
            className="text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.decisions.afterNote}
          </div>
          <div className="mt-1 flex justify-end gap-2.5">
            <Btn
              onClick={onCancel}
              tone="dim"
              data-testid="inbox-confirm-cancel"
            >
              {cs.common.cancel}
            </Btn>
            <Btn
              variant={danger ? "danger" : "primary"}
              disabled={pending}
              onClick={onConfirm}
              data-testid="inbox-confirm"
            >
              {cs.decisions.confirmSubmit}
            </Btn>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
